import React, { useContext, useEffect, useRef, useState } from "react";
import { PatioContext } from "context/patio-calculator";
import RadioProductsCards from "../../_components/radio-products-cards";
import DropdownProductsSelect from "../../_components/dropdown-products-select";
import { MEASUREMENTS_STATE } from "@root/constants/enums";
import ToggleableIcon from "@root/components/toggleable-icon";

const CustomizationContainer = ({ data, index }) => {
    const { customAttributes, updateConfiguration, setCustomAttributes, measurementsState } = useContext(PatioContext);
    const [showCustomization, setShowCustomization] = useState(false);
    let value = customAttributes[`c_${data.title}`];
    let variantId = data.variants.find((variant) => variant.title?.replace(/::.*$/, "") === value)?.id;

    const [currentKey, setCurrentKey] = useState(variantId || data.default_variant_id);
    // Mirror of currentKey updated synchronously so rapid successive clicks
    // don't read a stale closure value between renders.
    const currentKeyRef = useRef(currentKey);

    const updateCustomization = (variantId, title) => {
        const numericVariantId = Number(variantId);
        const numericCurrentKey = Number(currentKeyRef.current);

        // Functional dispatch so we derive from the latest configuration,
        // not a stale snapshot shared with other sibling customization
        // containers updating in parallel.
        updateConfiguration((state) => {
            const clearedVariants = (state.customization_variant_ids || [])
                .map((id) => Number(id))
                .filter((vid) => vid !== numericCurrentKey && vid !== numericVariantId);

            return {
                customization_variant_ids: [...clearedVariants, numericVariantId],
            };
        });

        setCustomAttributes((prevAttributes) => ({
            ...prevAttributes,
            [`c_${data.title}`]: title || "n/a",
        }));

        currentKeyRef.current = numericVariantId;
        setCurrentKey(numericVariantId);
    };

    useEffect(() => {
        if (measurementsState === MEASUREMENTS_STATE.FINISHED) {
            setShowCustomization(true);
        }
    }, [measurementsState]);

    const getVariants = () => data.variants.map((variant) => ({
        ...variant,
        title: variant.title.replace(/::.*$/, ""),
    }));

    useEffect(() => {
        if (data.default_variant_id && !variantId) {
            const defaultItem = data.variants.find((variant) => variant.id === data.default_variant_id);
            const replaceDefault = defaultItem?.title.replace(/::.*$/, "");
            updateCustomization(data.default_variant_id, replaceDefault);
        }
    }, [data.default_variant_id, data.variants, data.title]);

    const handleChange = (itemId, item) => {
        updateCustomization(itemId, item?.title);
    };

    return (
        <div className="configurator-section my-4">
            <div onClick={() => setShowCustomization(!showCustomization)} className="configurator-section__header">
                <h3 className="configurator-section__header-title">{`${index}. ${data?.title}`} <ToggleableIcon state={showCustomization} /> </h3>
            </div>
            <div className={`configurator-section__inner configurator-section__inner--toggle ${showCustomization ? "configurator-section__inner--show" : ""}`}>
                {data.type === "Grid" && (
                    <RadioProductsCards
                        items={getVariants()}
                        onChange={handleChange}
                        value={currentKey}
                    />
                )}
                {data.type === "Dropdown" && (
                    <DropdownProductsSelect
                        items={getVariants()}
                        onChange={handleChange}
                        value={currentKey}
                    />
                )}
            </div>
        </div>
    );
};

const Customization = ({ customizations = [], startIndex }) => (
    <>
        {customizations.map((customization, idx) => {
            const calculatedIndex = startIndex + idx;
            return (
                <CustomizationContainer
                    key={customization.id}
                    data={customization}
                    index={calculatedIndex}
                />
            );
        })}
    </>
);

export default Customization;
